import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { listMealPlanClientFeedback } from '@/lib/api/mealPlans';
import type {
  MealPlanFeedbackLevel,
  MealPlanFeedbackRecord,
} from '@/types/meal-plan';

type FeedbackFilter = MealPlanFeedbackLevel | 'all';

interface MealPlanClientFeedbackPanelProps {
  mealPlanId: string;
  mealPlanName?: string;
}

const FILTERS: Array<{ value: FeedbackFilter; label: string }> = [
  { value: 'all', label: 'All feedback' },
  { value: 'meal_plan', label: 'Whole plan' },
  { value: 'day', label: 'Day' },
  { value: 'meal_slot', label: 'Meal slot' },
  { value: 'recipe', label: 'Recipe' },
];

const LEVEL_META: Record<MealPlanFeedbackLevel, { label: string; color: string; background: string }> = {
  meal_plan: { label: 'Whole plan', color: '#175CD3', background: '#EFF8FF' },
  day: { label: 'Day', color: '#027A48', background: '#ECFDF3' },
  meal_slot: { label: 'Meal slot', color: '#B54708', background: '#FFFAEB' },
  recipe: { label: 'Recipe', color: '#6941C6', background: '#F4F3FF' },
};

function formatFeedbackDate(value: string | null): string {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date unavailable';
  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function formatTargetDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function dayLabel(item: MealPlanFeedbackRecord): string | null {
  const dayNumber = item.target.dayNumber;
  const dayName = item.target.dayName?.trim();
  if (dayNumber && dayName) return `Day ${dayNumber} · ${dayName}`;
  if (dayNumber) return `Day ${dayNumber}`;
  return dayName || null;
}

function targetTitle(item: MealPlanFeedbackRecord, fallbackPlanName?: string): string {
  switch (item.level) {
    case 'recipe':
      return item.target.recipeName?.trim() || 'Recipe';
    case 'meal_slot':
      return item.target.mealName?.trim() || item.target.mealType?.trim() || 'Meal slot';
    case 'day':
      return dayLabel(item) || 'Meal-plan day';
    default:
      return item.mealPlanName?.trim() || fallbackPlanName?.trim() || 'Entire meal plan';
  }
}

function targetContext(item: MealPlanFeedbackRecord): string {
  if (item.level === 'meal_plan') return 'Entire meal plan';
  if (item.level === 'day') return formatTargetDate(item.target.date) || '';

  const context = [dayLabel(item)];
  if (item.level === 'recipe') {
    context.push(item.target.mealName?.trim() || item.target.mealType?.trim() || null);
  }
  return context.filter((value): value is string => Boolean(value)).join('  /  ');
}

export const MealPlanClientFeedbackPanel: React.FC<MealPlanClientFeedbackPanelProps> = ({
  mealPlanId,
  mealPlanName,
}) => {
  const [filter, setFilter] = React.useState<FeedbackFilter>('all');
  const [items, setItems] = React.useState<MealPlanFeedbackRecord[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [error, setError] = React.useState('');
  const [loadMoreError, setLoadMoreError] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [totalCount, setTotalCount] = React.useState(0);
  const [hasNextPage, setHasNextPage] = React.useState(false);
  const requestSequence = React.useRef(0);

  const loadFeedback = React.useCallback(async (nextPage = 1, append = false) => {
    const requestId = ++requestSequence.current;
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError('');
    setLoadMoreError('');

    try {
      const response = await listMealPlanClientFeedback(mealPlanId, {
        ...(filter === 'all' ? {} : { level: filter }),
        page: nextPage,
        pageSize: 25,
      });
      if (requestId !== requestSequence.current) return;

      const feedback = response.data?.feedback ?? [];
      const pagination = response.data?.pagination;
      setItems((current) => (append ? [...current, ...feedback] : feedback));
      setPage(pagination?.page ?? nextPage);
      setTotalCount(pagination?.totalCount ?? feedback.length);
      setHasNextPage(Boolean(pagination?.hasNextPage));
    } catch (loadError) {
      if (requestId !== requestSequence.current) return;
      console.error('Failed to load client meal plan feedback:', loadError);
      if (append) {
        setLoadMoreError('More feedback could not be loaded. Please try again.');
      } else {
        setError('Client feedback could not be loaded right now.');
        setItems([]);
        setTotalCount(0);
        setHasNextPage(false);
      }
    } finally {
      if (requestId === requestSequence.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [filter, mealPlanId]);

  React.useEffect(() => {
    setItems([]);
    setPage(1);
    void loadFeedback(1, false);
  }, [loadFeedback]);

  return (
    <Paper
      component="section"
      elevation={0}
      aria-label="Client meal plan feedback"
      sx={{
        mt: 2,
        border: '1px solid #E4EAEE',
        borderRadius: '18px',
        bgcolor: '#FFFFFF',
        p: { xs: 1.5, md: 2 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: '10px',
                bgcolor: '#EAF7FC',
                color: '#168DB8',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 19 }} />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#1D2939' }}>
                Client feedback
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#667085' }}>
                Feedback on the plan, individual days, meal slots, and recipes.
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Button
          size="small"
          variant="text"
          startIcon={<RefreshRoundedIcon sx={{ fontSize: 17 }} />}
          onClick={() => void loadFeedback(1, false)}
          disabled={isLoading || isLoadingMore}
          sx={{
            textTransform: 'none',
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            color: '#475467',
          }}
        >
          Refresh
        </Button>
      </Box>

      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1.7 }}>
        {FILTERS.map((option) => {
          const selected = filter === option.value;
          return (
            <Button
              key={option.value}
              size="small"
              variant={selected ? 'contained' : 'outlined'}
              onClick={() => setFilter(option.value)}
              aria-pressed={selected}
              sx={{
                minHeight: 32,
                borderRadius: '999px',
                px: 1.35,
                textTransform: 'none',
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.78rem',
                fontWeight: 600,
                color: selected ? '#FFFFFF' : '#475467',
                bgcolor: selected ? '#25A8DA' : '#FFFFFF',
                borderColor: selected ? '#25A8DA' : '#D0D5DD',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: selected ? '#168DB8' : '#F9FAFB',
                  borderColor: selected ? '#168DB8' : '#98A2B3',
                  boxShadow: 'none',
                },
              }}
            >
              {option.label}
            </Button>
          );
        })}
      </Stack>

      {isLoading ? (
        <Box sx={{ minHeight: 132, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      ) : error ? (
        <Box sx={{ mt: 1.7, borderRadius: '12px', bgcolor: '#FEF3F2', px: 1.5, py: 1.3 }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.84rem', color: '#B42318' }}>
            {error}
          </Typography>
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            mt: 1.7,
            minHeight: 112,
            border: '1px dashed #D0D5DD',
            borderRadius: '12px',
            display: 'grid',
            placeItems: 'center',
            px: 2,
            textAlign: 'center',
            bgcolor: '#FCFCFD',
          }}
        >
          <Box>
            <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', fontWeight: 600, color: '#475467' }}>
              No client feedback yet
            </Typography>
            <Typography sx={{ mt: 0.25, fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', color: '#98A2B3' }}>
              Feedback submitted by the client will appear here.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Stack spacing={1} sx={{ mt: 1.7 }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', color: '#667085' }}>
            {totalCount} {totalCount === 1 ? 'entry' : 'entries'}
          </Typography>
          {items.map((item) => {
            const meta = LEVEL_META[item.level];
            const context = targetContext(item);
            return (
              <Box
                key={item.id}
                sx={{
                  border: '1px solid #EAECF0',
                  borderRadius: '12px',
                  px: { xs: 1.25, sm: 1.5 },
                  py: 1.3,
                  bgcolor: '#FFFFFF',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          borderRadius: '999px',
                          px: 0.9,
                          py: 0.25,
                          bgcolor: meta.background,
                          color: meta.color,
                          fontFamily: 'Inter, sans-serif',
                          fontSize: '0.68rem',
                          lineHeight: 1.5,
                          fontWeight: 700,
                        }}
                      >
                        {meta.label}
                      </Box>
                      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', fontWeight: 700, color: '#344054' }}>
                        {targetTitle(item, mealPlanName)}
                      </Typography>
                    </Stack>
                    {context ? (
                      <Typography sx={{ mt: 0.35, fontFamily: 'Inter, sans-serif', fontSize: '0.73rem', color: '#667085' }}>
                        {context}
                      </Typography>
                    ) : null}
                  </Box>
                  <Typography sx={{ flexShrink: 0, fontFamily: 'Inter, sans-serif', fontSize: '0.7rem', color: '#98A2B3' }}>
                    {formatFeedbackDate(item.createdAt)}
                  </Typography>
                </Box>

                {item.rating != null ? (
                  <Stack direction="row" spacing={0.65} alignItems="center" sx={{ mt: 0.9 }}>
                    <Rating value={item.rating} readOnly size="small" />
                    <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.72rem', color: '#667085' }}>
                      {item.rating}/5
                    </Typography>
                  </Stack>
                ) : null}

                <Typography
                  sx={{
                    mt: 0.85,
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'anywhere',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.84rem',
                    lineHeight: 1.55,
                    color: '#344054',
                  }}
                >
                  {item.feedbackText}
                </Typography>
              </Box>
            );
          })}

          {loadMoreError ? (
            <Typography sx={{ textAlign: 'center', fontFamily: 'Inter, sans-serif', fontSize: '0.76rem', color: '#B42318' }}>
              {loadMoreError}
            </Typography>
          ) : null}

          {hasNextPage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => void loadFeedback(page + 1, true)}
                disabled={isLoadingMore}
                sx={{
                  minWidth: 120,
                  textTransform: 'none',
                  borderColor: '#D0D5DD',
                  color: '#475467',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                }}
              >
                {isLoadingMore ? <CircularProgress size={18} /> : 'Load more'}
              </Button>
            </Box>
          ) : null}
        </Stack>
      )}
    </Paper>
  );
};
